import { createContext, useContext } from 'react'
import { GrowthCategory } from '@mc/api'
import type { CategoryDynamicHeroValues } from 'libs/dynamic-hero'
import {
  SubcategoryWithPreview,
  SubcatCoursesOutcome,
} from 'libs/server/api/growth/categories'
import { CategoryPageCourse } from 'libs/server/api/growth/content'
import { CourseOutcome } from 'types/data'
import type { TCourse } from '../../dataFetching'

// data passed in from server-side props
type PageData = {
  slug: string
  categories: GrowthCategory[]
  subcategories: SubcategoryWithPreview[]
  category: GrowthCategory | null
  courses?: CategoryPageCourse[]
  instructors?: string[]
  coursesOutcomes?: Record<string, CourseOutcome[]>
  subcatsCoursesOutcome?: SubcatCoursesOutcome[]
  categorySavedDynamicHero?: CategoryDynamicHeroValues
  dynamicSubcategoryNames?: Record<string, string> | null
}

// data fetched on the client side, below the fold
type BelowTheFoldData = {
  // TCourse type represents the minimal fields needed to display the instructor tiles.
  // these fields should be shared across courses and camps.
  content: TCourse[]
}

const PageDataContext = createContext<PageData | null>(null)

type DataProvider<T> = {
  value: T
  children: React.ReactNode
}

export const PageDataProvider = ({
  value,
  children,
}: DataProvider<PageData>) => (
  <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
)

export const usePageData = () => {
  const ctx = useContext(PageDataContext)
  if (!ctx) {
    throw new Error('Did you forget to wrap in PageContextProvider?')
  }
  return ctx
}

const BelowTheFoldDataContext = createContext<BelowTheFoldData | null>(null)

export const BelowTheFoldDataProvider = ({
  value,
  children,
}: DataProvider<BelowTheFoldData>) => (
  <BelowTheFoldDataContext.Provider value={value}>
    {children}
  </BelowTheFoldDataContext.Provider>
)

export const useBelowTheFoldData = () => {
  const ctx = useContext(BelowTheFoldDataContext)
  if (!ctx) {
    throw new Error('Did you forget to wrap in BelowTheFoldDataProvider?')
  }
  return ctx
}
